import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, ExternalLink, FileText, ArrowRight, QrCode as QrIcon, FilePlus2 } from "lucide-react";
import QrDisplay from "@/components/QrDisplay";
import EnviarDecaEmail, { BotonEnviarEmail } from "@/components/EnviarDecaEmail";
import { decaPdfPath, descargaUrl } from "@/lib/publicUrls";
import { api } from "@/lib/api";
import { decaToGenerado } from "@/lib/generarDeca";
import { fmtFecha } from "@/lib/fechas";
import { fmtEnvios, fmtNumeroDeca } from "@/lib/textos";

export default function GeneratedPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [emailId, setEmailId] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("deca_ultimos_generados");
    const abrir = sessionStorage.getItem("deca_abrir_email") === "1";
    sessionStorage.removeItem("deca_abrir_email");
    let parsed = [];
    if (raw) {
      try { parsed = JSON.parse(raw) || []; } catch { parsed = []; }
    }
    if (parsed.length) {
      setItems(parsed);
      if (abrir) setEmailId(parsed[0].deca_id);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/api/v1/decas", { params: { limit: 5 } });
        const rec = (data.items || []).filter((d) => d.estado !== "anulado").map(decaToGenerado);
        if (rec.length) {
          setItems(rec);
          sessionStorage.setItem("deca_ultimos_generados", JSON.stringify(rec));
          if (abrir) setEmailId(rec[0].deca_id);
        }
      } catch { /* se queda vacio */ }
    })();
  }, []);

  return (
    <div>
      <PageHeader
        title="DeCA generados"
        subtitle={"Estos documentos est" + String.fromCharCode(0xe1) + "n firmados electr" + String.fromCharCode(0xf3) + "nicamente y disponibles con QR de descarga directa. Gu" + String.fromCharCode(0xe1) + "rdalos, env" + String.fromCharCode(0xed) + "alos o desc" + String.fromCharCode(0xe1) + "rgalos."}
        testid="page-generados"
        actions={<>
          <Button variant="outline" onClick={()=>nav("/decas")}><FileText className="h-4 w-4 mr-2"/> Ir al hist{String.fromCharCode(0xf3)}rico</Button>
          <Button onClick={()=>nav("/albaranes")} className="bg-blue-700 hover:bg-blue-800">Desde Sage <ArrowRight className="h-4 w-4 ml-2"/></Button>
          <Button variant="outline" onClick={()=>nav("/decas/manual")}><FilePlus2 className="h-4 w-4 mr-2"/> DeCA manual</Button>
        </>}
      />

      {items.some((it) => it.estado === "borrador") ? (
        <div className="mb-4 border border-amber-200 bg-amber-50 rounded-md p-3 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-amber-700"/>
          <div>
            <div className="font-semibold text-amber-950">
              {items.filter((it) => it.estado === "borrador").length} borrador{items.filter((it) => it.estado === "borrador").length === 1 ? "" : "es"} guardado{items.filter((it) => it.estado === "borrador").length === 1 ? "" : "s"}
            </div>
            <div className="text-sm text-amber-900">A{String.fromCharCode(0xfa)}n no es el DeCA oficial. Abre el detalle, informa la matr{String.fromCharCode(0xed)}cula y em{String.fromCharCode(0xed)}telo.</div>
          </div>
        </div>
      ) : (
      <div className="mb-4 border border-emerald-200 bg-emerald-50 rounded-md p-3 flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 text-emerald-700"/>
        <div>
          <div className="font-semibold text-emerald-900">{items.length} DeCA emitidos correctamente</div>
          <div className="text-sm text-emerald-800">Cada QR contiene la URL p{String.fromCharCode(0xfa)}blica de descarga (sin auth), como exige la normativa.</div>
        </div>
      </div>
      )}

      {items.length === 0 ? (
        <Card className="p-6 text-sm text-slate-600">
          No hay un DeCA reci{String.fromCharCode(0xe9)}n generado en esta sesi{String.fromCharCode(0xf3)}n.{" "}
          <button type="button" className="text-blue-700 underline" onClick={()=>nav("/decas")}>Ir al hist{String.fromCharCode(0xf3)}rico</button>
        </Card>
      ) : null}

      <div className="grid md:grid-cols-2 xl:grid-cols-2 gap-4">
        {items.map((it) => {
          const token = it.download_token || String(it.public_url || "").split("/").pop();
          const descarga = token ? descargaUrl(token) : it.public_url;
          return (
          <Card key={it.deca_id} className="p-5" data-testid={`gen-card-${it.deca_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="label-tag">DeCA</div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>
                  {it.cargador?.razon_social} <span className="text-slate-400">{"\u2192"}</span> {it.transportista?.razon_social}
                </h3>
                <div className="text-xs text-slate-500 font-mono mt-0.5">{fmtNumeroDeca(it)}</div>
                <div className="text-xs text-slate-600 mt-2">
                  {fmtEnvios(it.num_envios)} {"\u00b7"} {fmtFecha(it.fecha_transporte)} {"\u00b7"} {(it.matriculas || []).join(", ") || "\u2014"}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={decaPdfPath(it.deca_id)} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" data-testid={`btn-pdf-${it.deca_id}`}><FileText className="h-4 w-4 mr-2"/> Ver PDF</Button>
                  </a>
                  {it.estado !== "borrador" ? (
                    <>
                      <a href={descarga} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" data-testid={`btn-public-${it.deca_id}`}><ExternalLink className="h-4 w-4 mr-2"/> URL p{String.fromCharCode(0xfa)}blica</Button>
                      </a>
                      <BotonEnviarEmail onClick={()=>setEmailId(it.deca_id)}/>
                    </>
                  ) : null}
                  <Button size="sm" onClick={()=>nav(`/decas/${it.deca_id}`)} className="bg-blue-700 hover:bg-blue-800"><QrIcon className="h-4 w-4 mr-2"/> {it.estado === "borrador" ? "Completar" : "Detalle + QR"}</Button>
                </div>
                {it.estado !== "borrador" && descarga ? <div className="mt-2 text-[11px] text-slate-400 break-all">{descarga}</div> : null}
              </div>
              {it.estado !== "borrador" ? (
              <div className="shrink-0">
                <QrDisplay value={descarga} size={128}/>
              </div>
              ) : null}
            </div>
          </Card>
          );
        })}
      </div>
      <EnviarDecaEmail decaId={emailId} open={!!emailId} onOpenChange={(o)=> { if (!o) setEmailId(null); }} />
    </div>
  );
}
